import { Fragment } from 'react';

import Img from 'components/Img';
import { OptionalLink } from 'components/Link';
import { PriceText } from 'components/Price';
import Text from 'components/Text';
import type { FormattedPrice } from 'models/api';
import { is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

interface Props {
	/** The amount per product in the summary */
	amount: number;

	/** The article number in the component */
	articleNumber?: string;

	/** Is there a discount? If there is, then render red color on the price */
	discount: boolean;

	/** The heading in the component */
	heading: string;

	imageAlt: string | undefined;

	/** Image src attribute */
	imageSrc?: string;

	/** The link to the product page */
	link?: string;

	placements?: string[];

	/** The total sum of the product row */
	rowSum?: FormattedPrice;
}

/** A product line for summaries */
export default function SummaryProductLine({
	amount,
	articleNumber,
	discount,
	heading,
	imageAlt,
	imageSrc,
	link,
	placements,
	rowSum,
}: Props) {
	const { t } = useI18n();

	const headingId = `summary-product-title-${articleNumber}`;

	return (
		<article
			className="break-inside-avoid border-b border-strong py-3 first-of-type:mt-4 first-of-type:border-t"
			aria-labelledby={headingId}
		>
			<div className="relative flex">
				<Img
					src={imageSrc}
					alt={imageAlt}
					width={40}
					height={40}
					className="mr-2 size-10 shrink-0"
				/>
				<div>
					<Text as="p" className="pr-4 font-bold" id={headingId}>
						<OptionalLink
							href={link}
							cover
							fallback={null}
							linkClassName="hover:underline"
						>
							{heading}
						</OptionalLink>
					</Text>
					<Text as="pSmall" className="mt-1 flex gap-x-4">
						{articleNumber && (
							<span>
								{t('general_article_number_label')}: {articleNumber}
							</span>
						)}
						<span>
							{t('general_quantity_label')}: {amount}
						</span>
					</Text>
					{is.arrayWithLength(placements) && (
						<Text as="pSmall" className="mt-1 flex gap-x-4">
							<span>{`${t('general_product_prints_label')}:`}</span>
							<span>
								{placements?.map((print, i) => (
									<Fragment key={print}>
										{print}
										{i < placements.length - 1 ? ' + ' : ''}
									</Fragment>
								))}
							</span>
						</Text>
					)}
				</div>
				<PriceText
					price={rowSum}
					isReducedPrice={discount}
					className="ml-auto font-bold"
				/>
			</div>
		</article>
	);
}
SummaryProductLine.displayName = 'SummaryProductLine';
